import { formatCellValue, humanizeLabel, pnlColor } from "../../lib/format";
import { CHART_INK, colorForSeries } from "../../lib/palette";

interface StatPanelProps {
	columns: string[];
	rows: Record<string, unknown>[];
	titleColumn: string;
}

// A handful of rows, each with many metric columns and one clear category
// (e.g. side = Yes/No) -- shown as side-by-side stat cards instead of an
// overflowing wide table.
export function StatPanel({ columns, rows, titleColumn }: StatPanelProps) {
	if (rows.length === 0) {
		return (
			<div className="text-xs font-mono text-base-content/50 py-6 text-center">
				No rows for this selection.
			</div>
		);
	}

	const statColumns = columns.filter((col) => col !== titleColumn);

	return (
		<div
			className={`grid gap-3 ${rows.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"}`}
		>
			{rows.map((row, i) => {
				const title = String(row[titleColumn] ?? "–");
				const dotColor = colorForSeries(title, i);
				return (
					<div
						key={i}
						className="rounded p-3"
						style={{
							border: `1px solid ${CHART_INK.gridline}`,
							background: "rgba(225,225,230,0.03)",
						}}
					>
						<div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-base-content/10">
							<span
								className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
								style={{ backgroundColor: dotColor }}
							/>
							<span className="text-xs font-bold font-mono text-base-content">
								{title}
							</span>
						</div>
						<dl className="flex flex-col gap-1">
							{statColumns.map((col) => {
								const cell = formatCellValue(row[col]);
								const color = pnlColor(col, row[col]);
								return (
									<div
										key={col}
										className="flex items-baseline justify-between gap-3 text-[11px] font-mono"
									>
										<dt className="text-base-content/50 whitespace-nowrap">
											{humanizeLabel(col)}
										</dt>
										<dd
											className="text-base-content/90 font-semibold tabular-nums text-right truncate"
											title={cell.full ?? undefined}
											style={color ? { color } : undefined}
										>
											{cell.display}
										</dd>
									</div>
								);
							})}
						</dl>
					</div>
				);
			})}
		</div>
	);
}
